import { z } from 'zod';

/**
 * Immutable lifecycle events captured in the source mutation transaction.
 * Generation is a separate CAS identity from eventId. ACL metadata is context;
 * current source visibility and exact generation are enforced again by Nest.
 * Live source text is fetched through the source-bound capability, not queued.
 */

// ── shared building blocks ─────────────────────────────────────────────

export const KnowledgeVisibility = z.enum(['public', 'team', 'project', 'restricted']);
export type KnowledgeVisibilityValue = z.infer<typeof KnowledgeVisibility>;

export const KnowledgeClassification = z.enum(['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED']);
export type KnowledgeClassificationValue = z.infer<typeof KnowledgeClassification>;

/** ACL snapshot frozen at the moment Projoint emitted the event.
 *  AI Core uses these fields to filter retrieval results before vector
 *  similarity ranking. */
export const KnowledgeAclSchema = z.object({
  visibility: KnowledgeVisibility,
  classification: KnowledgeClassification,
  /** Team that owns the content.  Null only for cross-team admin pages. */
  teamId: z.string().uuid().nullable(),
  /** Project the content belongs to (when applicable). */
  projectId: z.string().uuid().nullable(),
  /** Role presets allowed to see the content (e.g. ['OWNER','MANAGER']).
   *  Empty array means "team default" (no extra restriction beyond
   *  team membership). */
  allowedRolePresets: z.array(z.string()).default([]),
  /** Explicit user IDs the content is shared with — for personal /
   *  direct-message content + per-row sharing. */
  allowedUserIds: z.array(z.string().uuid()).default([]),
});
export type KnowledgeAcl = z.infer<typeof KnowledgeAclSchema>;

const Source = z.object({
  /** Stable URI mirroring the MCP resource scheme so AI Core can fetch
   *  the live row via MCP later: projoint://tasks/{uuid}, etc. */
  uri: z.string(),
  /** Human-friendly source label for citations rendered in UI. */
  label: z.string(),
});

const Base = z.object({
  /** UUID identifying this specific event — idempotency key. */
  eventId: z.string().uuid(),
  /** ISO timestamp of when Projoint emitted the event. */
  emittedAt: z.string(),
  /** Monotonic source CAS generation; required on every active ingest event. */
  sourceGeneration: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  source: Source,
  acl: KnowledgeAclSchema,
});

// ── per-content-kind events ────────────────────────────────────────────

export const KnowledgeTaskChangedSchema = Base.extend({
  kind: z.literal('task.changed'),
  taskId: z.string().uuid(),
  payload: z.object({
    title: z.string(),
    descriptionText: z.string().nullable(),
    statusCategory: z.string(),
    /** Project key + task number so AI Core can render "ACME-42" in
     *  citations without hitting MCP. */
    key: z.string(),
  }),
});

export const KnowledgeTaskDeletedSchema = Base.extend({
  kind: z.literal('task.deleted'),
  taskId: z.string().uuid(),
});

export const KnowledgeCommentAddedSchema = Base.extend({
  kind: z.literal('comment.added'),
  commentId: z.string().uuid(),
  taskId: z.string().uuid(),
  payload: z.object({
    bodyText: z.string(),
    /** Author display name — pre-resolved so AI Core can attribute
     *  citations without an extra MCP fetch. */
    authorName: z.string(),
  }),
});

export const KnowledgeCommentDeletedSchema = Base.extend({
  kind: z.literal('comment.deleted'),
  commentId: z.string().uuid(),
  taskId: z.string().uuid(),
});

export const KnowledgeChatMessagePostedSchema = Base.extend({
  kind: z.literal('chat.message_posted'),
  messageId: z.string().uuid(),
  topicId: z.string().uuid(),
  payload: z.object({
    bodyText: z.string(),
    authorName: z.string(),
  }),
});

export const KnowledgeChatMessageDeletedSchema = Base.extend({
  kind: z.literal('chat.message_deleted'),
  messageId: z.string().uuid(),
  topicId: z.string().uuid(),
});

export const KnowledgeFileUploadedSchema = Base.extend({
  kind: z.literal('file.uploaded'),
  fileId: z.string().uuid(),
  payload: z.object({
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    /** Extracted plaintext if Projoint already ran extraction (small
     *  files / inline office docs).  Null when AI Core should pull
     *  contents itself via MCP files/{fileId}/content (Phase 2 next). */
    extractedText: z.string().nullable(),
  }),
});

export const KnowledgeFileDeletedSchema = Base.extend({
  kind: z.literal('file.deleted'),
  fileId: z.string().uuid(),
});

export const KnowledgePageChangedSchema = Base.extend({
  kind: z.literal('page.changed'),
  pageId: z.string().uuid(),
  payload: z.object({ title: z.string(), bodyText: z.string() }),
});

export const KnowledgePageDeletedSchema = Base.extend({
  kind: z.literal('page.deleted'),
  pageId: z.string().uuid(),
});

export const KnowledgeProjectAclChangedSchema = Base.extend({
  kind: z.literal('project.acl_changed'),
  projectId: z.string().uuid(),
  /** AI Core repaints ACL on every chunk tagged with this projectId. */
});

// ── discriminated union ────────────────────────────────────────────────

export const KnowledgeEventSchema = z.discriminatedUnion('kind', [
  KnowledgeTaskChangedSchema,
  KnowledgeTaskDeletedSchema,
  KnowledgeCommentAddedSchema,
  KnowledgeCommentDeletedSchema,
  KnowledgeChatMessagePostedSchema,
  KnowledgeChatMessageDeletedSchema,
  KnowledgeFileUploadedSchema,
  KnowledgeFileDeletedSchema,
  KnowledgePageChangedSchema,
  KnowledgePageDeletedSchema,
  KnowledgeProjectAclChangedSchema,
]);
export type KnowledgeEvent = z.infer<typeof KnowledgeEventSchema>;

/** Ingest endpoint accepts batches (1-100 events at a time).  AI Core
 *  responds 202 Accepted once events are persisted to its ingestion
 *  queue; actual chunking + embedding happens async. */
export const KnowledgeIngestBatchSchema = z.object({
  events: z.array(KnowledgeEventSchema).min(1).max(100),
});
export type KnowledgeIngestBatch = z.infer<typeof KnowledgeIngestBatchSchema>;

export const KnowledgeIngestResponseSchema = z.object({
  accepted: z.number().int().nonnegative(),
  /** Per-event positional acknowledgement. Event ids are diagnostic only:
   * publishers must bind each result to the exact request-array position so a
   * duplicate id cannot redirect an acknowledgement to another event. */
  results: z.array(
    z.object({
      position: z.number().int().nonnegative(),
      eventId: z.string().uuid(),
      status: z.enum(['queued', 'duplicate', 'rejected']),
      reason: z.string().nullable().default(null),
    }),
  ),
});
export type KnowledgeIngestResponse = z.infer<typeof KnowledgeIngestResponseSchema>;
